// @flow

import React, {useState, useEffect, useRef} from 'react';
import PropTypes from 'prop-types';
import {
  makeStyles,
  Typography,
  CircularProgress,
  Chip,
} from '@material-ui/core';
import CustomModal from '../../../../../elements/CustomModal/CustomModal';
import {colors} from '../../../../../assets/styles/colors';
import CustomButton from '../../../../../elements/CustomButton/CustomButton';

import type {ConversationTag} from '../../../../../entities';
import type {Theme} from '@material-ui/core';
import {useConversationTags} from '../../utils/conversationTag';
import ConfirmModal from '../../../../../elements/Modals/ConfirmModal';
import TagInput from '../../../../../elements/TagInput';

type Props = {
  open: boolean,
  handleClose: () => void,
  tags: ConversationTag[],
  onSave: (selectedTags: ConversationTag[]) => void,
};

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    marginTop: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: 480,
    maxWidth: '100%',
    position: 'relative',
  },
  loader: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    background: '#ffffff80',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  title: {
    fontSize: 18,
    color: colors.grey900,
    fontWeight: 600,
  },
  content: {
    margin: theme.spacing(1.5, 0),
  },
  greyText: {
    fontSize: 14,
    color: '#9595AB',
  },
  labelsList: {
    margin: theme.spacing(1.5, 0),
  },
  labelChip: {
    border: '1px solid #E8E8ED',
    borderRadius: 30,
    backgroundColor: '#FFFFFF',
  },
  button: {
    width: '10rem',
  },
  buttonWrap: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  tagList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '.5rem',
  },
}));

export default function AddConversationLabelModal(props: Props) {
  const {open, handleClose, tags = [], onSave} = props;
  const classes = useStyles();
  const {
    createTag,
    deleteTag,
    loading,
    conversationTags,
  } = useConversationTags();

  const conversationTagToDelete = useRef<null | string>(null);

  const [selectedTags, setSelectedTags] = useState(tags.map(({tag}) => tag));

  const [innerTags, setInnerTags] = useState(
    conversationTags.map(({tag}) => tag)
  );
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const openConfirmModal = () => setConfirmModalOpen(true);
  const closeConfirmModal = () => setConfirmModalOpen(false);

  useEffect(() => {
    const newTags = innerTags.filter(
      innerTag => !conversationTags.find(({tag}) => tag === innerTag)
    );

    if (!newTags.length || !!conversationTagToDelete.current) return;

    createTag(newTags).then((tags: ConversationTag[]) => {
      setSelectedTags(selectedTags => [
        ...selectedTags,
        ...tags.map(({tag}) => tag),
      ]);
    });
  }, [conversationTags, createTag, innerTags]);

  const deleteConversationTag = () => {
    const tagToDelete = conversationTags.find(
      ({tag}) => tag === conversationTagToDelete.current
    );

    if (!tagToDelete) return;

    deleteTag(tagToDelete.id).then(() => {
      setInnerTags(innerTags.filter(tag => tag !== tagToDelete.tag));
      conversationTagToDelete.current = null;
    });
  };

  const handleSave = async () => {
    onSave(conversationTags.filter(({tag}) => selectedTags.includes(tag)));
    handleClose();
  };

  const handleSelectTag = (tag: string) => {
    if (selectedTags.includes(tag)) {
      setSelectedTags(selectedTags.filter(t => t !== tag));
    } else {
      setSelectedTags([...selectedTags, tag]);
    }
  };

  return (
    <>
      <CustomModal
        open={open}
        handleCloseModal={handleClose}
        keepMounted={false}
      >
        <div className={classes.root}>
          {loading && (
            <div className={classes.loader}>
              <CircularProgress color="primary" />
            </div>
          )}
          <Typography className={classes.title}>
            Crear etiqueta de conversación
          </Typography>
          <div className={classes.content}>
            <Typography className={classes.greyText} align="center">
              Crea etiquetas para clasificar las conversaciones con tus
              clientes.
            </Typography>

            <div className={classes.labelsList}>
              <TagInput
                tagList={innerTags.map(tag => ({
                  label: tag,
                  value: tag,
                }))}
                onListChange={tagList => {
                  const newTags = tagList.map(({value}) => value);
                  setInnerTags(newTags);
                }}
                renderTags={tagList => (
                  <div className={classes.tagList}>
                    {tagList.map((item, index) => {
                      const option = item.value;

                      return (
                        <Chip
                          label={item.label}
                          key={item.value}
                          variant="outlined"
                          onClick={() => {
                            handleSelectTag(option);
                          }}
                          onDelete={() => {
                            openConfirmModal();
                            conversationTagToDelete.current = option;
                          }}
                          color={
                            selectedTags.includes(option)
                              ? 'primary'
                              : 'default'
                          }
                        />
                      );
                    })}
                  </div>
                )}
              />
            </div>
            <div className={classes.buttonWrap}>
              <CustomButton
                onClick={handleSave}
                className={classes.button}
                variant="contained"
                disabled={loading}
              >
                Guardar
              </CustomButton>
            </div>
          </div>
        </div>
      </CustomModal>
      <ConfirmModal
        title="¿Estás seguro que deseas eliminar la etiqueta de conversación?"
        open={confirmModalOpen}
        message="Se eliminará esta etiqueta de todas las conversaciones."
        onClose={() => {
          closeConfirmModal();
        }}
        onCancel={() => {
          closeConfirmModal();
        }}
        onConfirm={() => {
          deleteConversationTag();
          closeConfirmModal();
        }}
      />
    </>
  );
}

AddConversationLabelModal.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
};
